import { useCallback, useMemo } from "react";
import { useAuth } from "@/auth";
import type { AutocompleteResponse, TripPlannerRequest, TripPlannerResponse } from "@/types/tripPlanner";

class TripPlannerApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "TripPlannerApiError";
    this.status = status;
  }
}

/**
 * Both callers and the returned object are memoised. `autocomplete` reaches a billed Google
 * endpoint, and a fresh function identity on every render is enough to re-trigger a caller's
 * effect/query forever — that was the #79 runaway loop. Keep these stable.
 */
export function useTripPlannerApi() {
  const { authorizedFetch } = useAuth();

  const calculate = useCallback(
    async (body: TripPlannerRequest): Promise<TripPlannerResponse> => {
      const res = await authorizedFetch("/api/trip-planner/calculate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let msg = `calculate ${res.status}`;
        try {
          const errBody = (await res.json()) as { error?: string };
          if (errBody.error) msg = errBody.error;
        } catch {
          /* ignore */
        }
        throw new TripPlannerApiError(msg, res.status);
      }
      return (await res.json()) as TripPlannerResponse;
    },
    [authorizedFetch],
  );

  const autocomplete = useCallback(
    async (input: string, sessionToken: string): Promise<AutocompleteResponse> => {
      const res = await authorizedFetch("/api/google/autocomplete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ input, session_token: sessionToken }),
      });
      if (!res.ok) throw new TripPlannerApiError(`autocomplete ${res.status}`, res.status);
      return (await res.json()) as AutocompleteResponse;
    },
    [authorizedFetch],
  );

  return useMemo(() => ({ calculate, autocomplete }), [calculate, autocomplete]);
}
